<template>
  <div id="knw_widget_input" class="knw_widget_input" :class="{ isGrayedOut: grayedOut }">
    <!-- TOAST (INPUT) STACK -->
    <transition name="slide_up">
      <Toast
        v-if="showToast"
        :show-toast="showToast"
        :connection-state="botState.level"
        :toast-message="botState.text"
      />
    </transition>
    <!-- SECONDARY INPUT STACK -->
    <!-- QR, MASKED INPUT, ... -->
    <transition name="slide_up">
      <QuickReplies
        v-if="hasQuickReplies"
        key="quickreplies"
        :quick-replies="quickReplies"
        @on-intent="handleIntent"
      />
    </transition>
    <!-- PRIMARY INPUT STACK -->
    <!-- TEXTINPUT OR CANCEL ACTION FO SECONDARY / SPECIALIZED INPUT (MASKED INPUT / QR / ...) -->
    <transition name="switchinput" mode="out-in">
      <TextInput
        v-if="getInputType.type === 'textinput'"
        key="textinput"
        :max-char-count="inputCharacterLimit"
        :has-input-focus="widgetStore.hasInputFocus"
        :capabilities="getInputType?.capabilities"
        @on-text-input="handleTextInput"
        @request-input-type="handleRequestInputType"
      />
      <DelayedCancelInput
        v-else-if="getInputType.type === 'cancel'"
        key="blocked_cancel"
        :intent="getInputType?.action"
        :label="getInputType?.label"
        @on-cancel="handleCancel"
      />
      <!-- We can assert that botId, channelId and jwtToken are present at this moment - so assert non null -->
      <FileInput
        v-else-if="getInputType.type === 'fileupload' && getInputType.options"
        key="fileupload"
        :options="getInputType.options"
        :max-height="maxHeight"
        :bot-id="botId!"
        :channel-id="channelId!"
        :jwt-token="getJWTToken()!"
        @on-cancel="(intent) => handleCancel(intent, true)"
        @on-complete="handleComplete"
        @on-conversation-info="handleConversationInfo"
      />

      <DisabledInput v-else-if="getInputType.type === 'disabled'" key="disbaled" />
    </transition>

    <PoweredBy v-if="widgetStore.showPoweredBy" />
  </div>
</template>
<script setup lang="ts">
import DelayedCancelInput from "@/components/inputs/DelayedCancelInput/DelayedCancelInput.vue";
import DisabledInput from "@/components/inputs/DisabledInput.vue";
import FileInput from "@/components/inputs/FileUpload/FileInput.vue";
import QuickReplies from "@/components/inputs/QuickReplies/QuickReplies.vue";
import TextInput from "@/components/inputs/TextInput/TextInput.vue";
import Toast from "@/components/notifications/Toast.vue";

import { computed, ref } from "vue";
import logger from "@/logger";
import { useMoinBot } from "@/services/bot/botv2";
import { watch } from "vue";
import type { Intent } from "@/types/Intent";
import type { ChatInputType } from "@/types";
import { useWidgetStore } from "@/store/widget";
import PoweredBy from "@/components/widgetParts/PoweredBy.vue";
import { storeToRefs } from "pinia";

interface Props {
  maxHeight?: number;
  grayedOut?: boolean;
}

withDefaults(defineProps<Props>(), { maxHeight: 450, grayedOut: false });

const {
  connectionStatus,
  hasQuickReplies,
  quickReplies,
  getBotInfo,
  getJWTToken,
  botId,
  channelId,
  getInputType,
  sendIntent,
  requestInputType,
  restorePreviousInputType,
  sendTextInput,
  addConversationInfo,
} = useMoinBot();
const widgetStore = useWidgetStore();
const { inputCharacterLimit } = storeToRefs(widgetStore);

const showToast = ref(false);
const botState = computed(() => {
  switch (connectionStatus.value) {
    case "offline":
      return { level: "offline", text: "Verbindung verloren" };
    case "reconnect":
      return { level: "warning", text: "reconnecting" };
    case "error":
      return { level: "offline", text: "error connecting" };
    case "reconnected":
    case "online":
      return { level: "online", text: "I am back online" };
    default:
      return { level: "online", text: "this should never happen" };
  }
});

watch(connectionStatus, (newValue) => {
  showToast.value = true;
  if (newValue === "online") {
    setTimeout(() => {
      showToast.value = false;
    }, 2000);
  }
});

// Below maybe should be a state machine
// I tried it already in a earlier attempt at another place and it became a mess
// So for now I will keep it simple and this will work for now
// But as soon as we have more complex states and input elements we should refactor this

const handleRequestInputType = (type: ChatInputType) => {
  requestInputType(type);
};

const handleCancel = (intent: Intent, api = false) => {
  const isInLiveChat = getBotInfo().livechat; // Do not send anything if in LiveChat Conversation

  if (intent && !isInLiveChat) {
    /**
     * For cancel to work *properly* for file upload cancel, it also needs to
     * have a text property. The "Cancel" message won't be added to conversation
     * history, but we need to skip adding it client-side.
     *
     * For other types of cancel, it works fine without the text property, but it
     * would appear in conversation history if we added it, so we must omit it.
     */
    if (!intent.text && intent.intent?.name === "upload_cancel") {
      const newIntent = { ...intent };
      newIntent.text = "Cancel";
      sendIntent(newIntent, api, { skipOptimisticUpdate: true });
    } else {
      sendIntent(intent, api);
    }
  }

  // Shouldn't have problems. But if any bugs will appear, add condition if (getBotInfo()?.livechat)
  restorePreviousInputType();
};

const handleComplete = (intent: Intent) => {
  if (intent) sendIntent(intent, true);
  restorePreviousInputType();
};

const handleIntent = (intent: Intent) => {
  if (!intent) logger.warn("no intent given");
  sendIntent(intent);
  // after sending the intent we request the default input type
  // this is needed in the quick replies case for example
  requestInputType({ type: "textinput" });
};

const handleTextInput = (text: string) => {
  sendTextInput(text);
};

const handleConversationInfo = (message: string) => {
  addConversationInfo(message);
};
</script>

<style lang="scss" scoped>
.knw_widget_input {
  position: absolute;
  bottom: 0;
  height: auto;
  width: 100%;
  justify-content: flex-end;
  min-height: 50px;
  max-height: 450px;
  z-index: 90;
  padding: 8px;
  padding-top: 0;
  // padding: 5px 0 5px 0;
  /* background-color: $kw-global-background-input; */
  background-color: var(--moin-inputs-background, $kw-global-background-input);
  background: transparent;
  transition: height $kw-default-transition-speed ease-out;
  // &:before {
  //   pointer-events: none;
  //   content:'';
  //   position: absolute;
  //   width: 100%;
  //   height: 30px;
  //   background: linear-gradient(to top, $kw-global-background-input, transparent);
  //   top: -30px;
  //   left: 0;
  // }
}

.knw_widget_inputbar {
  position: relative;
  min-height: 50px;
  //padding: 10px 0;
  z-index: 100;
  //transform: translateX(0);
  background: none;
  padding: 5px 8px;

  textarea {
    padding: 0 14px;
    border: none;
    // background: $kw-global-background-white;
    background-color: $kw-global-background-input;
    background-color: green;
    resize: none;
    width: 100%;
    height: auto;
    outline: none;
    font: 16px "Rubik", "Helvetica", "Arial", sans-serif;
    color: #333;

    &::placeholder {
      color: #aaaaaa;
    }
  }

  .knw_widget_inputbar_btn {
    width: 35px;
    height: 35px;
    background: transparent;
    border: none;
    padding: 0;
    margin-right: 8px;
    font-size: 1.6rem;
  }

  > svg {
    width: 35px;
    height: 35px;
    max-width: 35px;
    max-height: 35px;
    display: block;
  }
}

// slide up (quick replies)
.slide_up-enter {
  height: 0 !important;
  opacity: 0;
}

.slide_up-enter-active {
  transition: height 0.4s ease-in-out,
    opacity $kw-default-transition-speed ease-in-out $kw-default-transition-delay;
}

.slide_up-enter-to {
  opacity: 1;
}

.slide_up-leave {
  opacity: 1;
}

.slide_up-leave-active {
  transition: height 0.5s ease-in-out, opacity $kw-default-transition-speed ease-in-out;
}

.slide_up-leave-to {
  opacity: 0;
  height: 0 !important;
}

// switch input
.switchinput-enter {
  opacity: 0;
  transform: translateY(50px);
}

.switchinput-enter-active {
  transition: all 0.2s ease-out;
}

.switchinput-enter-to {
  transform: translateY(0px);
  opacity: 1;
}

.switchinput-leave {
  opacity: 1;
  transform: translateY(0px);
}

.switchinput-leave-active {
  transition: all 0.2s ease-out;
}

.switchinput-leave-to {
  opacity: 0;
  /*  transform: translateY(50px);*/
}

.isGrayedOut:after {
  content: "";
  filter: brightness(0.7);
  background-color: var(--moin-widgetbody-background, #eeeeee);

  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: -1;
}
</style>
